/**
 * Company Search — pattern read (sealed element g), live via
 * get_company_pattern_reads.
 *
 * Authored patterns render with their sealed connective language VERBATIM —
 * every text field is printed exactly as returned, never paraphrased,
 * summarized, or reformatted, and no numeric confidence is shown (the
 * contract's confidence_level is evidence-based text). Multiple patterns render
 * as a list. Orgs with none keep the honest empty state. A failed background
 * refetch keeps the cached patterns rather than masking them with an error.
 */

import { useCompanyPatternReads } from "../hooks";
import type { CompanyPatternReadView } from "../types";
import { IconPattern } from "./icons";

function Field({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div className="sxco-pattern-field">
      <span className="sxco-pattern-flabel">{label}</span>
      <span className="sxco-pattern-fval">{value}</span>
    </div>
  );
}

function PatternCard({ read }: { read: CompanyPatternReadView }): JSX.Element {
  return (
    <article className="sxco-pattern-card">
      <p className="sxco-pattern-name">{read.patternName}</p>
      {/* Verbatim: the sealed connective sentence, printed exactly as authored. */}
      <p className="sxco-pattern-lead">{read.expectedNextDevelopment}</p>
      {read.whyNow && <Field label="Why now" value={read.whyNow} />}
      {read.pursuitSurface && <Field label="Pursuit surface" value={read.pursuitSurface} />}
      {read.recommendedNextAction && (
        <Field label="Recommended next action" value={read.recommendedNextAction} />
      )}
      {read.watchFor.length > 0 && (
        <div className="sxco-pattern-field">
          <span className="sxco-pattern-flabel">Watch for</span>
          <ul className="sxco-pattern-watch">
            {read.watchFor.map((item, i) => (
              <li key={i}>{item}</li>
            ))}
          </ul>
        </div>
      )}
      {(read.evidenceRequirement || read.confidenceLevel) && (
        <div className="sxco-pattern-evidence">
          {read.evidenceRequirement && <span>{read.evidenceRequirement}</span>}
          {read.confidenceLevel && (
            <span className="sxco-pattern-confidence">{read.confidenceLevel}</span>
          )}
        </div>
      )}
    </article>
  );
}

export function PatternRead({ organizationId }: { organizationId: string }): JSX.Element {
  const { data, isPending, isError, error } = useCompanyPatternReads(organizationId);
  const reads = data ?? [];

  return (
    <section className="sxco-pattern">
      <p className="sxco-card-head">
        <span className="sxco-card-icon" aria-hidden="true">
          <IconPattern />
        </span>
        Pattern read
        {reads.length > 0 && <span className="sxco-count">{reads.length}</span>}
      </p>

      {isPending && <p className="sxco-muted">Loading pattern reads…</p>}

      {/* Error only when nothing ever loaded: a failed background refetch keeps
          a known result — including a known-empty one — rather than masking it. */}
      {isError && data === undefined && (
        <p className="sxco-error">
          {error instanceof Error ? error.message : "Failed to load pattern reads."}
        </p>
      )}

      {data !== undefined && reads.length === 0 && (
        <p className="sxco-muted">No pattern read has been authored for this account.</p>
      )}

      {reads.length > 0 && (
        <div className="sxco-pattern-list">
          {reads.map((read, i) => (
            <PatternCard key={i} read={read} />
          ))}
        </div>
      )}
    </section>
  );
}
